import type { ItemAvailabilityEntry } from "../../../packages/common/src/schemas/item-availability.ts";

export type AvailabilityChange = {
  readonly itemId: string;
  readonly available: boolean;
};

/** Apply one switch to the loaded list; unknown items are left for the next reload. */
export function withAvailability(
  items: readonly ItemAvailabilityEntry[],
  change: AvailabilityChange,
): readonly ItemAvailabilityEntry[] {
  return items.map((item) =>
    item.id === change.itemId && item.available !== change.available
      ? { ...item, available: change.available }
      : item,
  );
}

/**
 * The market whose switches to show: the one asked for if it exists, else the
 * last one used here, else the first.
 */
export function chooseMarket(
  markets: readonly { readonly id: string }[],
  ...preferred: readonly (string | null | undefined)[]
): string | undefined {
  const known = new Set(markets.map((market) => market.id));
  return preferred.find((id) => id && known.has(id)) ?? markets[0]?.id;
}

const lastMarketKey = "landpad.menu.market";

/** The market last shown on the Menu page on this device, if storage allows. */
export function lastMarket(): string | null {
  try {
    return localStorage.getItem(lastMarketKey);
  } catch {
    return null;
  }
}

export function rememberMarket(id: string) {
  try {
    localStorage.setItem(lastMarketKey, id);
  } catch {
    /* Optional: the page falls back to the first market. */
  }
}

/** Items grouped by their Square category, in a stable alphabetical order. */
export function byCategory(items: readonly ItemAvailabilityEntry[]) {
  const groups = new Map<string, ItemAvailabilityEntry[]>();
  for (const item of items)
    groups.set(item.category, [...(groups.get(item.category) ?? []), item]);

  return [...groups]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([category, entries]) => ({
      category,
      items: entries.sort((left, right) => left.name.localeCompare(right.name)),
    }));
}
